/**
 * discordant.org audio engine.
 *
 * An AudioWorklet modal chime synthesiser (public/worklet/chime-processor.js) drowned in a
 * procedurally generated cathedral (./reverb.ts). Quiet by design.
 *
 * State machine
 *   enabled  — the visitor's preference, persisted in localStorage 'discordant.sound'
 *   unlocked — the AudioContext is running after a user gesture
 *   audible  — enabled && unlocked
 * Every change is published on the bus as 'audio'. unlock() is only ever called from a
 * gesture: the context is created lazily there, resumed, the worklet module loaded and the
 * graph built. If any of that rejects, unlocked stays false and the failure is logged once.
 *
 * Graph
 *   worklet ─► dry ─────────────────────────┐
 *   worklet ─► pre-delay ─► convolver ─► wet ┴► master ─► compressor ─► trim ─► soft clip ─► out
 *                                                                      └► analysers (UI meter)
 * Muting ramps `master` to silence over ~150 ms and then suspends the context; nothing here
 * ever clicks. The compressor's built-in makeup gain is measured on an OfflineAudioContext
 * at build time and cancelled by `trim`, so CONFIG.audio.masterDb means what it says.
 */
import type { Bus } from '../bus';
import type { AudioState, ChimeWorld, Quality } from '../types';
import { CONFIG, DEV, readParams } from '../config';
import { BAR_PARTIALS } from '../physics/geometry';
import { clamp, dbToGain } from '../util/math';
import { generateCathedralIRAsync } from './reverb';
import { CHIME_PROCESSOR_NAME } from './messages';
import type { ChimeMessage, ChimeProcessorOptions } from './messages';

export interface AudioOptions {
  bus: Bus;
  quality: Quality;
  tuningHz: number[];
  tuningName: string;
  /** sound wanted unless the visitor has said otherwise (localStorage may override) */
  defaultEnabled: boolean;
  /**
   * Force sound off for this visit regardless of the stored preference (?mute). When
   * omitted the engine reads the URL itself so ?mute wins over a stored "on".
   */
  forceMuted?: boolean;
}

export interface AudioHandle {
  readonly state: AudioState;
  /** call from a user gesture: creates/resumes the AudioContext if sound is enabled */
  unlock(): Promise<void>;
  setEnabled(enabled: boolean): void;
  toggle(): void;
  /** per-frame: wind bed level, sympathetic coupling, detune mirror */
  update(dt: number, world: ChimeWorld): void;
  /** page hidden / shown */
  suspend(): void;
  resume(): void;
  /** smoothed output level per channel, 0..1, for UI meters (zeros when not audible) */
  levels(): { l: number; r: number };
}

/* ---------------------------------------------------------------- knobs */
const STORAGE_KEY = 'discordant.sound';
const WORKLET_PATH = 'worklet/chime-processor.js';
/** mute/unmute ramp, seconds; context suspends this long after a mute begins */
const RAMP_S = 0.15;
const SUSPEND_DELAY_MS = 240;
const PRE_DELAY_S = 0.018;
/** how often the detune/damping/gust mirror is posted to the worklet */
const STATE_INTERVAL_S = 0.05;
/** dry level given the configured wet amount */
const DRY_GAIN = 1 - CONFIG.audio.wet * 0.6;
/** extra trim on the wet path (the IR is energy-normalised with most of that energy in the chimes' own bands; at 0.25 the wet path of a strike peaks ≈ 1 dB under its dry peak, measured headless) */
const WET_TRIM = 0.25;
/** stereo spread: anchors at ±ring.rx land at ±PAN_SPREAD */
const PAN_SPREAD = 0.7;
const COMPRESSOR = { threshold: -18, ratio: 3, knee: 20, attack: 0.01, release: 0.4 } as const;
/** below this the soft clipper is exactly linear; input 1.0 lands at ≈ 0.88 */
const CLIP_KNEE = 0.5;
const LITE_REVERB_S = 5;
const REVERB_SEED = 0x0c47;
/** fallback for the compressor's makeup gain if it cannot be measured (≈ +5 dB) */
const MAKEUP_FALLBACK = 1.8;
/** meter smoothing: attack/release per call at ~60 Hz */
const METER_ATTACK = 0.5, METER_RELEASE = 0.08;

interface Graph {
  ctx: AudioContext;
  node: AudioWorkletNode;
  master: GainNode;
  dry: GainNode;
  wet: GainNode;
  pre: DelayNode;
  conv: ConvolverNode;
  comp: DynamicsCompressorNode;
  trim: GainNode;
  shaper: WaveShaperNode;
  anL: AnalyserNode;
  anR: AnalyserNode;
}

function urlForcesMute(): boolean {
  try { return typeof location !== 'undefined' && readParams().mute; } catch { return false; }
}

function softClipCurve(knee: number, n = 4097): Float32Array<ArrayBuffer> {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const ax = Math.abs(x);
    const y = ax <= knee ? ax : knee + (1 - knee) * Math.tanh((ax - knee) / (1 - knee));
    c[i] = x < 0 ? -y : y;
  }
  return c;
}

function applyCompressor(comp: DynamicsCompressorNode) {
  comp.threshold.value = COMPRESSOR.threshold;
  comp.ratio.value = COMPRESSOR.ratio;
  comp.knee.value = COMPRESSOR.knee;
  comp.attack.value = COMPRESSOR.attack;
  comp.release.value = COMPRESSOR.release;
}

/**
 * The Web Audio compressor applies a fixed makeup gain derived from its parameters
 * (WebKit-lineage kernel, shared by Firefox and Chromium). Measure it with a -40 dBFS sine
 * so we can cancel it exactly on whatever browser this is.
 */
async function measureCompressorMakeup(sampleRate: number): Promise<number> {
  try {
    if (typeof OfflineAudioContext !== 'function') return MAKEUP_FALLBACK;
    const len = Math.round(sampleRate * 0.5);
    const off = new OfflineAudioContext(1, len, sampleRate);
    const osc = off.createOscillator();
    osc.frequency.value = 1000;
    const g = off.createGain();
    g.gain.value = 0.01;
    const comp = off.createDynamicsCompressor();
    applyCompressor(comp);
    osc.connect(g).connect(comp).connect(off.destination);
    osc.start();
    const buf = await off.startRendering();
    const d = buf.getChannelData(0);
    const from = Math.round(len * 0.6);
    let s = 0;
    for (let i = from; i < len; i++) s += d[i] * d[i];
    const rms = Math.sqrt(s / (len - from));
    const gain = rms / (0.01 * Math.SQRT1_2);
    return Number.isFinite(gain) && gain > 0.25 && gain < 16 ? gain : MAKEUP_FALLBACK;
  } catch {
    return MAKEUP_FALLBACK;
  }
}

function buildGraph(ctx: AudioContext, makeup: number, opts: AudioOptions, pans: number[] | null): Graph {
  const processorOptions: ChimeProcessorOptions = {
    tier: opts.quality.tier,
    tuningHz: opts.tuningHz.slice(),
    partials: BAR_PARTIALS.slice(),
    seed: CONFIG.seed ^ 0x51a7,
    ...(pans ? { pans } : {}),
  };
  const node = new AudioWorkletNode(ctx, CHIME_PROCESSOR_NAME, {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [2],
    processorOptions,
  });
  node.onprocessorerror = (e) => console.error('[audio] chime processor error', e);

  const dry = ctx.createGain();
  dry.gain.value = DRY_GAIN;
  const pre = ctx.createDelay(0.1);
  pre.delayTime.value = PRE_DELAY_S;
  const conv = ctx.createConvolver();
  conv.normalize = false; // the IR is energy-normalised by reverb.ts; keep the wet level deterministic
  // conv.buffer is attached by unlock() once the IR has been rendered
  const wet = ctx.createGain();
  wet.gain.value = CONFIG.audio.wet * WET_TRIM;
  const master = ctx.createGain();
  master.gain.value = 0; // ramped up by applyRunning()
  const comp = ctx.createDynamicsCompressor();
  applyCompressor(comp);
  const trim = ctx.createGain();
  trim.gain.value = clamp(1 / makeup, 0.1, 1);
  const shaper = ctx.createWaveShaper();
  shaper.curve = softClipCurve(CLIP_KNEE);
  shaper.oversample = opts.quality.tier === 'full' ? '2x' : 'none';
  const splitter = ctx.createChannelSplitter(2);
  const anL = ctx.createAnalyser();
  const anR = ctx.createAnalyser();
  anL.fftSize = anR.fftSize = 256;

  node.connect(dry).connect(master);
  node.connect(pre).connect(conv).connect(wet).connect(master);
  master.connect(comp).connect(trim).connect(shaper).connect(ctx.destination);
  trim.connect(splitter);
  splitter.connect(anL, 0);
  splitter.connect(anR, 1);

  return { ctx, node, master, dry, wet, pre, conv, comp, trim, shaper, anL, anR };
}

export function createAudio(opts: AudioOptions): AudioHandle {
  const forced = opts.forceMuted ?? urlForcesMute();
  let stored: string | null = null;
  try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* private mode */ }
  const state: AudioState = {
    enabled: forced ? false : stored === null ? opts.defaultEnabled : stored === '1',
    unlocked: false,
    audible: false,
  };

  const masterGain = dbToGain(CONFIG.audio.masterDb);
  const reverbSeconds = opts.quality.tier === 'full' ? CONFIG.audio.reverbSeconds : LITE_REVERB_S;
  const n = opts.tuningHz.length;
  const detune: number[] = new Array<number>(n).fill(0);
  const damped: number[] = new Array<number>(n).fill(0);

  let ctx: AudioContext | null = null;
  let graph: Graph | null = null;
  let pending: Promise<void> | null = null;
  let moduleLoaded: Promise<void> | null = null;
  let irPromise: Promise<AudioBuffer> | null = null;
  let hidden = typeof document !== 'undefined' && document.hidden;
  let world: ChimeWorld | null = null;
  let pans: number[] | null = null;
  let pansSent = false;
  let acc = 0;
  let suspendTimer = 0;
  let warned = false;
  let lastPublished = '';
  const meter = { l: 0, r: 0 };
  const meterBuf = new Float32Array(256);

  const publish = () => {
    state.audible = state.enabled && state.unlocked;
    const key = `${state.enabled}${state.unlocked}`;
    if (key === lastPublished) return;
    lastPublished = key;
    opts.bus.emit('audio', { ...state });
  };

  const warnOnce = (err: unknown) => {
    if (warned) return;
    warned = true;
    console.warn('[audio] sound unavailable:', err);
  };

  const post = (m: ChimeMessage) => {
    if (graph) graph.node.port.postMessage(m);
  };

  const ramp = (param: AudioParam, target: number) => {
    if (!ctx) return;
    const now = ctx.currentTime;
    const cur = param.value;
    param.cancelScheduledValues(now);
    param.setValueAtTime(cur, now);
    param.linearRampToValueAtTime(target, now + RAMP_S);
  };

  const wantRunning = () => state.enabled && state.unlocked && !hidden;

  const applyRunning = () => {
    if (!ctx || !graph) return;
    const c = ctx;
    if (suspendTimer) { clearTimeout(suspendTimer); suspendTimer = 0; }
    if (wantRunning()) {
      if (c.state !== 'running') void c.resume().catch(warnOnce);
      ramp(graph.master.gain, masterGain);
    } else {
      ramp(graph.master.gain, 0);
      suspendTimer = window.setTimeout(() => {
        suspendTimer = 0;
        if (!wantRunning() && c.state === 'running') void c.suspend().catch(() => { /* ignore */ });
      }, SUSPEND_DELAY_MS);
    }
  };

  const computePans = (w: ChimeWorld): number[] => {
    const cx = w.ring.center.x;
    const rx = w.ring.rx || 1;
    return w.tubes.map((t) => clamp((t.anchor.x - cx) / rx, -1, 1) * PAN_SPREAD);
  };

  const sendPans = () => {
    if (!graph || !pans) return;
    post({ type: 'pan', pans });
    pansSent = true;
  };

  async function unlock(): Promise<void> {
    if (!state.enabled || state.unlocked) return;
    if (pending) return pending;
    if (!ctx) {
      try {
        ctx = new AudioContext({ latencyHint: 'interactive' });
      } catch (err) {
        warnOnce(err);
        return;
      }
    }
    const c = ctx;
    // resume() must be issued synchronously inside the gesture (Safari cares)
    const resumed = c.resume();
    pending = (async () => {
      await resumed;
      if (c.state !== 'running') throw new Error(`AudioContext is ${c.state}`);
      if (!graph) {
        const t0 = performance.now();
        if (!moduleLoaded) {
          moduleLoaded = c.audioWorklet.addModule(import.meta.env.BASE_URL + WORKLET_PATH);
        }
        // the room is rendered in chunks alongside; sound starts dry the instant the
        // worklet is up and the reverb attaches as soon as it exists (~150 ms later)
        if (!irPromise) irPromise = generateCathedralIRAsync(c, reverbSeconds, { seed: REVERB_SEED });
        const makeupP = measureCompressorMakeup(c.sampleRate);
        let makeup: number;
        try {
          [, makeup] = await Promise.all([moduleLoaded, makeupP]);
        } catch (err) {
          moduleLoaded = null; // let a later gesture retry a failed fetch
          throw err;
        }
        const g = buildGraph(c, makeup, opts, pans);
        graph = g;
        void irPromise.then((ir) => {
          g.conv.buffer = ir;
          if (DEV) console.info(`[audio] reverb attached at +${(performance.now() - t0).toFixed(0)} ms (${reverbSeconds} s, ${(ir.length / 1000).toFixed(0)}k samples/ch)`);
        }).catch((err) => { irPromise = null; warnOnce(err); });
        if (DEV) {
          console.info(`[audio] ready in ${(performance.now() - t0).toFixed(0)} ms — ${c.sampleRate} Hz, compressor makeup ${(20 * Math.log10(makeup)).toFixed(1)} dB cancelled, tuning "${opts.tuningName}"`);
        }
      }
      state.unlocked = true;
      publish();
      if (!pansSent) sendPans();
      applyRunning();
    })();
    try {
      await pending;
    } catch (err) {
      warnOnce(err);
    } finally {
      pending = null;
    }
  }

  function setEnabled(enabled: boolean) {
    if (state.enabled === enabled) return;
    state.enabled = enabled;
    try { localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0'); } catch { /* ignore */ }
    publish();
    applyRunning();
  }

  opts.bus.on('strike', (e) => {
    if (!state.audible || !graph) return;
    post({ type: 'strike', tube: e.tube, velocity: e.velocity, s: e.s, kind: e.kind });
  });
  opts.bus.on('damp', (e) => {
    if (!state.audible || !graph) return;
    post({ type: 'damp', tube: e.tube, amount: e.amount });
  });

  const handle: AudioHandle = {
    state,
    unlock,
    setEnabled,
    toggle() { setEnabled(!state.enabled); },
    update(dt: number, w: ChimeWorld) {
      if (w !== world) {
        world = w;
        pans = computePans(w);
        pansSent = false;
      }
      if (!graph || !state.audible) return;
      if (!pansSent) sendPans();
      acc += dt;
      if (acc < STATE_INTERVAL_S) return;
      acc = 0;
      const st = w.tubeStates;
      const m = Math.min(n, st.length);
      for (let i = 0; i < m; i++) {
        detune[i] = st[i].detuneCents;
        damped[i] = st[i].damped;
      }
      post({ type: 'state', detune, damped, gust: w.gust });
    },
    suspend() {
      hidden = true;
      applyRunning();
    },
    resume() {
      hidden = false;
      applyRunning();
    },
    levels() {
      if (!graph || !state.audible || hidden) {
        meter.l = meter.r = 0;
        return meter;
      }
      const read = (an: AnalyserNode, prev: number) => {
        an.getFloatTimeDomainData(meterBuf);
        let s = 0;
        for (let i = 0; i < meterBuf.length; i++) s += meterBuf[i] * meterBuf[i];
        const rms = Math.sqrt(s / meterBuf.length);
        const db = 20 * Math.log10(rms + 1e-9);
        const v = clamp((db + 54) / 42, 0, 1);
        return prev + (v - prev) * (v > prev ? METER_ATTACK : METER_RELEASE);
      };
      meter.l = read(graph.anL, meter.l);
      meter.r = read(graph.anR, meter.r);
      return meter;
    },
  };

  publish();
  return handle;
}
